import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from '@/components/ui/dialog';
import { ControlledPassword } from '@/components/FormInputs';
import CustomButton from '@/components/CustomButton';

interface ChangePasswordFormData {
  newPassword: string;
  currentPassword: string;
}

interface ChangePasswordDialogProps {
  onSubmit: (newPassword: string, currentPassword: string) => void;
  onSuccess?: () => void;
  isPending?: boolean;
}

export default function ChangePasswordDialog({
  onSubmit,
  onSuccess,
  isPending = false,
}: ChangePasswordDialogProps) {
  const { t } = useTranslation();

  const changePasswordSchema = yup.object({
    newPassword: yup
      .string()
      .min(8, t('userMenu.changePassword.dialog.passwordMinLength'))
      .required(t('userMenu.changePassword.dialog.passwordRequired')),
    currentPassword: yup
      .string()
      .required(t('userMenu.changePassword.dialog.currentPasswordRequired')),
  });

  const { control, handleSubmit, reset } = useForm<ChangePasswordFormData>({
    resolver: yupResolver(changePasswordSchema),
    defaultValues: {
      newPassword: '',
      currentPassword: '',
    },
  });

  const wasPendingRef = useRef(false);

  const onFormSubmit = (data: ChangePasswordFormData) => {
    onSubmit(data.newPassword, data.currentPassword);
  };

  // Mutation başarılı olduğunda (isPending false'a döndüğünde ve önceden pending ise) dialog'u kapat
  useEffect(() => {
    if (wasPendingRef.current && !isPending && onSuccess) {
      onSuccess();
      reset();
    }
    wasPendingRef.current = isPending;
  }, [isPending, onSuccess, reset]);

  return (
    <DialogPopup>
      <DialogHeader>
        <DialogTitle>{t('userMenu.changePassword.dialog.title')}</DialogTitle>
        <DialogDescription>
          {t('userMenu.changePassword.dialog.description')}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogPanel className="space-y-4">
          <ControlledPassword
            control={control}
            name="currentPassword"
            label={t('userMenu.changePassword.dialog.currentPassword')}
            placeholder={t('userMenu.changePassword.dialog.currentPasswordPlaceholder')}
            required
          />
          <ControlledPassword
            control={control}
            name="newPassword"
            label={t('userMenu.changePassword.dialog.newPassword')}
            placeholder={t('userMenu.changePassword.dialog.newPasswordPlaceholder')}
            required
          />
        </DialogPanel>
      </form>
      <DialogFooter>
        <DialogClose
          render={
            <CustomButton
              label={t('userMenu.changePassword.dialog.cancel')}
              type="button"
              variant="ghost"
              disabled={isPending}
            />
          }
        />
        <CustomButton
          label={t('userMenu.changePassword.dialog.changePassword')}
          type="submit"
          disabled={isPending}
          loading={isPending}
          onClick={handleSubmit(onFormSubmit)}
        />
      </DialogFooter>
    </DialogPopup>
  );
}
